import { labelData, TrendItem } from "../../interface/DesignerInterface";
import {
  getTrendCurveLineColor,
  TREND_CURVE_HALF_YEAR_PERIOD_REGEX,
  TREND_CURVE_QUARTER_PERIOD_REGEX,
  TREND_CURVE_QUARTER_RANGES,
  TREND_CURVE_YEAR_PERIOD_REGEX,
} from "../../constants/trendCurveConstants";
import { filterArchivedGraphRows } from "../archivedTrends";

type TrendCurveDatasetInput = {
  trend: string;
  tNo: number;
  data: number[];
};

const createDataset = (trends: TrendCurveDatasetInput[]) => {
  return trends.map(({ trend, tNo, data }, index) => ({
    label: trend,
    tNo,
    data,
    fill: false,
    borderColor: getTrendCurveLineColor(trend, index),
    tension: 0.1,
    borderWidth: 1,
    pointRadius: 0,
    pointHoverRadius: 2,
  }));
};

export const formatTrendCurvePeriodLabel = (period: string): string => {
  if (period.includes(",")) {
    const [monthLabel, yearLabel] = period.split(",").map(part => part.trim());
    return `${monthLabel} ${yearLabel}`;
  }

  const quarterMatch = period.match(TREND_CURVE_QUARTER_PERIOD_REGEX);
  if (quarterMatch) {
    const quarter = Number(quarterMatch[1]);
    const year = quarterMatch[2];
    const range = TREND_CURVE_QUARTER_RANGES[quarter - 1];
    return range ? `${range} ${year}` : period;
  }

  const halfYearMatch = period.match(TREND_CURVE_HALF_YEAR_PERIOD_REGEX);
  if (halfYearMatch) {
    const half = Number(halfYearMatch[1]);
    const year = halfYearMatch[2];
    return half === 1 ? `Jan–Jun ${year}` : `Jul–Dec ${year}`;
  }

  const yearMatch = period.match(TREND_CURVE_YEAR_PERIOD_REGEX);
  if (yearMatch) {
    return yearMatch[1];
  }

  return period;
};

const parseTrendCurvePeriodDate = (period: string): number => {
  if (period.includes(",")) {
    return new Date(`1 ${period.replace(",", "")}`).getTime();
  }

  const quarterMatch = period.match(TREND_CURVE_QUARTER_PERIOD_REGEX);
  if (quarterMatch) {
    const quarter = Number(quarterMatch[1]);
    const year = Number(quarterMatch[2]);
    return new Date(year, (quarter - 1) * 3, 1).getTime();
  }

  const halfYearMatch = period.match(TREND_CURVE_HALF_YEAR_PERIOD_REGEX);
  if (halfYearMatch) {
    const half = Number(halfYearMatch[1]);
    const year = Number(halfYearMatch[2]);
    return new Date(year, (half - 1) * 6, 1).getTime();
  }

  const yearMatch = period.match(TREND_CURVE_YEAR_PERIOD_REGEX);
  if (yearMatch) {
    return new Date(Number(yearMatch[1]), 0, 1).getTime();
  }

  return 0;
};

type TrendCurvePeriodRange = { start: number; end: number };

/**
 * Resolve a curve period label to the [start, end) timestamp span it covers.
 * Supports month ("Aug, 2026"), quarter ("Q3 2026"), half-year ("H2 2026") and
 * year ("2026") labels. Returns null for unrecognised formats.
 */
const getTrendCurvePeriodRange = (
  period: string
): TrendCurvePeriodRange | null => {
  if (period.includes(",")) {
    const startDate = new Date(`1 ${period.replace(",", "")}`);
    if (Number.isNaN(startDate.getTime())) return null;

    return {
      start: startDate.getTime(),
      end: new Date(
        startDate.getFullYear(),
        startDate.getMonth() + 1,
        1
      ).getTime(),
    };
  }

  const quarterMatch = period.match(TREND_CURVE_QUARTER_PERIOD_REGEX);
  if (quarterMatch) {
    const quarter = Number(quarterMatch[1]);
    const year = Number(quarterMatch[2]);
    return {
      start: new Date(year, (quarter - 1) * 3, 1).getTime(),
      end: new Date(year, (quarter - 1) * 3 + 3, 1).getTime(),
    };
  }

  const halfYearMatch = period.match(TREND_CURVE_HALF_YEAR_PERIOD_REGEX);
  if (halfYearMatch) {
    const half = Number(halfYearMatch[1]);
    const year = Number(halfYearMatch[2]);
    return {
      start: new Date(year, (half - 1) * 6, 1).getTime(),
      end: new Date(year, (half - 1) * 6 + 6, 1).getTime(),
    };
  }

  const yearMatch = period.match(TREND_CURVE_YEAR_PERIOD_REGEX);
  if (yearMatch) {
    const year = Number(yearMatch[1]);
    return {
      start: new Date(year, 0, 1).getTime(),
      end: new Date(year + 1, 0, 1).getTime(),
    };
  }

  return null;
};

const parseTrendCurveRangeBoundary = (
  value: string,
  boundary: "start" | "end"
): number => {
  const datePart = value.trim().split(/[T ]/)[0];
  return new Date(
    `${datePart} ${boundary === "start" ? "00:00:00" : "23:59:59.999"}`
  ).getTime();
};

const sortTrendCurvePeriods = (periods: labelData[]): labelData[] =>
  [...periods].sort(
    (a, b) =>
      parseTrendCurvePeriodDate(a.period) - parseTrendCurvePeriodDate(b.period)
  );

export const sortTrendCurvePeriodsForDisplay = (
  periods: labelData[]
): labelData[] => sortTrendCurvePeriods(periods);

/** Accepts API (`start_date`) or snapshot preference (`startDate`) shapes. */
export type TrendCurveDateRangeLike = {
  start_date?: string | null;
  end_date?: string | null;
  startDate?: string | null;
  endDate?: string | null;
};

/**
 * When the request defines an analysis start (start-only or start+end), keep
 * every period in that window — including trailing/leading zeros — so the
 * chart x-axis matches the user's selected range. Rolling windows still trim.
 * Mirrors backend `shouldPreserveAllCurvePeriods`.
 */
export const shouldPreserveAllCurvePeriods = (
  dateRange?: TrendCurveDateRangeLike | null
): boolean => {
  const start = dateRange?.start_date ?? dateRange?.startDate;
  return Boolean(start && String(start).trim());
};

/**
 * Drop leading/trailing zero-contribution periods. Interior empty periods
 * in the active range are kept so the x-axis stays continuous.
 * When `archivedTrendNames` is provided, archived trends are filtered out first.
 * When `preserveAllPeriods` is true (explicit start date), skip trimming.
 */
export const filterActiveTrendCurvePeriods = (
  trendCurveResponse: TrendItem[],
  archivedTrendNames?: string[],
  preserveAllPeriods?: boolean
): TrendItem[] => {
  const response =
    archivedTrendNames && archivedTrendNames.length
      ? filterArchivedGraphRows(trendCurveResponse, archivedTrendNames)
      : trendCurveResponse;

  if (!response?.length || !response[0]?.data?.length) {
    return response ?? [];
  }

  if (preserveAllPeriods) {
    return response;
  }

  const periodCount = response[0].data.length;
  const isActive = (index: number) =>
    response.some(trend => (trend.data[index]?.contribution ?? 0) > 0);

  let firstActive = -1;
  let lastActive = -1;

  for (let index = 0; index < periodCount; index++) {
    if (!isActive(index)) continue;
    if (firstActive < 0) firstActive = index;
    lastActive = index;
  }

  if (firstActive < 0) {
    return response;
  }

  if (firstActive === 0 && lastActive === periodCount - 1) {
    return response;
  }

  return response.map(trend => ({
    ...trend,
    data: trend.data.slice(firstActive, lastActive + 1),
  }));
};

/**
 * Filter trend curve periods by the date range selected in the snapshot
 * preferences. A period is kept when the span it covers intersects the range,
 * so a yearly/quarterly period that contains the range is not dropped just
 * because its start date falls before `startDate`.
 * Periods with an unrecognised label are kept so unexpected backend formats
 * never blank out the chart.
 */
export const filterTrendCurveByDateRange = (
  trendCurveResponse: TrendItem[],
  dateRange?: TrendCurveDateRangeLike | null
): TrendItem[] => {
  const start = dateRange?.start_date ?? dateRange?.startDate;
  const end = dateRange?.end_date ?? dateRange?.endDate;

  if (!start && !end) {
    return trendCurveResponse;
  }

  const parsedStartTime = start
    ? parseTrendCurveRangeBoundary(String(start), "start")
    : -Infinity;
  const parsedEndTime = end
    ? parseTrendCurveRangeBoundary(String(end), "end")
    : Infinity;
  const startTime = Number.isNaN(parsedStartTime) ? -Infinity : parsedStartTime;
  const endTime = Number.isNaN(parsedEndTime) ? Infinity : parsedEndTime;

  return trendCurveResponse
    .map(trend => ({
      ...trend,
      data: trend.data.filter(point => {
        const periodRange = getTrendCurvePeriodRange(point.period);
        if (!periodRange) return true;
        return periodRange.end > startTime && periodRange.start <= endTime;
      }),
    }))
    .filter(trend => trend.data.length > 0);
};

export const getFactoredTrendCurveResponse = (
  trendCurveResponse: TrendItem[],
  _curveWindowType?: string,
  archivedTrendNames?: string[],
  preserveAllPeriods?: boolean
) => {
  try {
    const filteredTrendCurveResponse = filterActiveTrendCurvePeriods(
      trendCurveResponse,
      archivedTrendNames,
      preserveAllPeriods
    );

    if (
      !filteredTrendCurveResponse?.length ||
      !filteredTrendCurveResponse[0]?.data?.length
    ) {
      return { labels: [], datasets: [] };
    }

    const sortedTrends = [...filteredTrendCurveResponse].sort(
      (a, b) => (a.tNo ?? 0) - (b.tNo ?? 0)
    );

    const sortedPeriods = sortTrendCurvePeriods(sortedTrends[0].data);
    const labels = sortedPeriods.map((item: labelData) =>
      formatTrendCurvePeriodLabel(item.period)
    );

    const trendDatasets: TrendCurveDatasetInput[] = sortedTrends.map(item => {
      const pointsByPeriod = new Map<string, number>(
        item.data.map((point: labelData) => [point.period, point.contribution])
      );

      return {
        trend: item.Trend,
        tNo: item.tNo,
        data: sortedPeriods.map(
          periodPoint => pointsByPeriod.get(periodPoint.period) ?? 0
        ),
      };
    });

    return {
      labels,
      datasets: createDataset(trendDatasets),
    };
  } catch (error) {
    console.log("Error Factoring Trends Curves Data", error);
    return {
      labels: [],
      datasets: [],
    };
  }
};
